import React, { useState, useEffect, useRef } from 'react';
import SkillEditorBody from './SkillEditorBody';
import AgentBundleItemModal, { type BundleItem } from './AgentBundleItemModal';
import { resolveAgentShare, type AgentShareSnapshot } from '../features/claudeagents/claudeAgentApi';

const chipStyle: React.CSSProperties = { cursor: 'pointer', border: 'none', font: 'inherit' };

// Read-only preview of a shared agent bundle (system prompt + skills + MCP
// requirements) — fetched from the Python-owned share snapshot. Shared across
// MyToolsPage's marketplace detail page, its Creations detail panel, the
// Review Queue's pending-submission view, and a resolved agent share.
// showSkillContent is forwarded as-is to AgentBundleItemModal (see that
// component's comment for what it gates and why it defaults to true).
const AgentBundlePreview: React.FC<{ token: string; showSkillContent?: boolean }> = ({ token, showSkillContent = true }) => {
  const [snapshot, setSnapshot] = useState<AgentShareSnapshot | null>(null);
  const [error, setError] = useState('');
  const previewEditorRef = useRef<HTMLTextAreaElement>(null);
  const rulesEditorRef = useRef<HTMLTextAreaElement>(null);
  const [selectedItem, setSelectedItem] = useState<BundleItem | null>(null);

  useEffect(() => {
    let cancelled = false;
    resolveAgentShare(token)
      .then(r => { if (!cancelled) setSnapshot(r.snapshot); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not load agent bundle'); });
    return () => { cancelled = true; };
  }, [token]);

  if (error) return <p style={{ color: '#f87171', fontSize: 12.5, margin: 0 }}>{error}</p>;
  if (!snapshot) return <p style={{ color: 'var(--text-tertiary)', fontSize: 12.5, margin: 0 }}>Loading bundle…</p>;

  return (
    <>
      {snapshot.system && (
        <div>
          <div className="mt-detail-field-label">agents.md</div>
          <SkillEditorBody
            mode="preview"
            onModeChange={() => {}}
            body={snapshot.system}
            onBodyChange={() => {}}
            textEditorRef={previewEditorRef}
            insertMd={() => {}}
            readOnly
            hideModeTabs
          />
        </div>
      )}
      {snapshot.user_rules && (
        <div>
          <div className="mt-detail-field-label">Your Rules</div>
          <SkillEditorBody
            mode="preview"
            onModeChange={() => {}}
            body={snapshot.user_rules}
            onBodyChange={() => {}}
            textEditorRef={rulesEditorRef}
            insertMd={() => {}}
            readOnly
            hideModeTabs
          />
        </div>
      )}
      {snapshot.skills.length > 0 && (
        <div>
          <div className="mt-detail-field-label">Bundled skills</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {snapshot.skills.map(s => (
              <button key={s.name} type="button" onClick={() => setSelectedItem({ kind: 'skill', name: s.name, content: s.content })}
                style={{ ...chipStyle, background: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.22)', color: '#fb923c', fontSize: 11, padding: '2px 8px', borderRadius: 5 }}>
                {s.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {snapshot.mcp_servers.length > 0 && (
        <div>
          <div className="mt-detail-field-label">Bundled MCPs</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
            {snapshot.mcp_servers.map(m => (
              <button key={m.url} type="button" title={m.needs_auth ? 'Requires credentials' : undefined}
                onClick={() => setSelectedItem({ kind: 'mcp', name: m.name, url: m.url, needs_auth: m.needs_auth })}
                style={{ ...chipStyle, background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.22)', color: '#60a5fa', fontSize: 11, padding: '2px 8px', borderRadius: 5 }}>
                {m.name}{m.needs_auth ? ' 🔑' : ''}
              </button>
            ))}
          </div>
        </div>
      )}
      {selectedItem && <AgentBundleItemModal item={selectedItem} onClose={() => setSelectedItem(null)} showSkillContent={showSkillContent} />}
    </>
  );
};

export default AgentBundlePreview;
